import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import ru from 'emoji-picker-react/dist/data/emojis-ru';
import { EmojiIcon } from '@/utils/icons';
import useChatMentions from './hooks/useChatMentions';
import s from './ChatTextInput.module.css';

const ChatTextInput = forwardRef(({
    members = [],
    onChange,
    onSubmit,
    placeholder = 'Напишите сообщение',
    value = '',
    disabled = false,
}, ref) => {
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const inputAreaRef = useRef(null);
    const textareaRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const emojiPickerPopupRef = useRef(null);
    const {
        activeMentionIndex,
        handleMentionKeyDown,
        mentionOptions,
        mentionSearch,
        resetMentions,
        selectMention,
        serializeMentions,
        updateMentionSearch,
    } = useChatMentions({
        members,
        message: value,
        setMessage: onChange,
        textareaRef,
    });

    const insertTextAtCursor = (text) => {
        const textarea = textareaRef.current;

        if (!textarea) {
            onChange(`${value}${text}`);
            return;
        }

        const start = textarea.selectionStart ?? value.length;
        const end = textarea.selectionEnd ?? value.length;
        const nextMessage = `${value.slice(0, start)}${text}${value.slice(end)}`;

        onChange(nextMessage);
        updateMentionSearch(nextMessage, start + text.length);

        requestAnimationFrame(() => {
            const nextCursorPosition = start + text.length;
            textarea.focus();
            textarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
        });
    };

    const wrapSelection = (before, after = before) => {
        const textarea = textareaRef.current;
        const currentValue = String(value ?? '');

        if (!textarea) {
            onChange(`${currentValue}${before}${after}`);
            return;
        }

        const start = textarea.selectionStart ?? currentValue.length;
        const end = textarea.selectionEnd ?? currentValue.length;
        const selectedText = currentValue.slice(start, end);
        const fallbackText = selectedText || 'текст';
        const nextMessage = `${currentValue.slice(0, start)}${before}${fallbackText}${after}${currentValue.slice(end)}`;
        const nextSelectionStart = start + before.length;
        const nextSelectionEnd = nextSelectionStart + fallbackText.length;

        onChange(nextMessage);
        updateMentionSearch(nextMessage, nextSelectionEnd);

        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(nextSelectionStart, nextSelectionEnd);
        });
    };

    const onEmojiClick = (emojiData) => {
        insertTextAtCursor(emojiData.emoji);
        setIsEmojiPickerOpen(false);
    };

    const resetInputState = () => {
        resetMentions();
        setIsEmojiPickerOpen(false);
    };

    const clear = () => {
        onChange('');
        resetInputState();
    };

    const getSerializedValue = () => serializeMentions(value);

    useImperativeHandle(ref, () => ({
        clear,
        focus: () => textareaRef.current?.focus(),
        getSerializedValue,
        resetInputState,
    }), [onChange, value, serializeMentions]);

    const resizeTextarea = useCallback(() => {
        const textarea = textareaRef.current;

        if (!textarea) return;

        textarea.style.height = '0px';
        textarea.style.height = `${Math.max(40, Math.min(textarea.scrollHeight, 150))}px`;
    }, []);

    useEffect(() => {
        resizeTextarea();
    }, [resizeTextarea, value]);

    useEffect(() => {
        const inputArea = inputAreaRef.current;

        if (!inputArea || typeof ResizeObserver === 'undefined') return;

        let inputAreaWidth = -1;
        const resizeObserver = new ResizeObserver(([entry]) => {
            const nextWidth = entry.contentRect.width;

            if (nextWidth === inputAreaWidth) return;

            inputAreaWidth = nextWidth;
            resizeTextarea();
        });

        resizeObserver.observe(inputArea);

        return () => resizeObserver.disconnect();
    }, [resizeTextarea]);

    useEffect(() => {
        const onClickOutside = (event) => {
            const isInsideTrigger = emojiPickerRef.current?.contains(event.target);
            const isInsidePopup = emojiPickerPopupRef.current?.contains(event.target);

            if (!isInsideTrigger && !isInsidePopup) {
                setIsEmojiPickerOpen(false);
            }
        };

        if (!isEmojiPickerOpen) return;

        document.addEventListener('mousedown', onClickOutside);

        return () => {
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [isEmojiPickerOpen]);

    return (
        <div ref={inputAreaRef} className={s.inputArea}>
            {mentionSearch.start >= 0 && mentionOptions.length > 0 && (
                <div className={s.mentionsPopup}>
                    {mentionOptions.map((member, index) => (
                        <button
                            key={member.user_id}
                            type='button'
                            className={classNames(s.mentionOption, {
                                [s.mentionOptionActive]: index === activeMentionIndex,
                            })}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectMention(member)}>
                            <span className={s.mentionName}>{member.name}</span>
                        </button>
                    ))}
                </div>
            )}
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(event) => {
                    if (disabled) return;

                    const nextValue = event.target.value;
                    onChange(nextValue);
                    updateMentionSearch(nextValue, event.target.selectionStart);
                }}
                onClick={(event) => updateMentionSearch(event.target.value, event.target.selectionStart)}
                onKeyUp={(event) => {
                    if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
                        return;
                    }

                    updateMentionSearch(event.currentTarget.value, event.currentTarget.selectionStart);
                }}
                onKeyDown={(event) => {
                    if (disabled) return;

                    if (handleMentionKeyDown(event)) {
                        return;
                    }

                    if (event.ctrlKey || event.metaKey) {
                        const key = event.key.toLowerCase();

                        if (key === 'b') {
                            event.preventDefault();
                            wrapSelection('**');
                            return;
                        }

                        if (key === 'i') {
                            event.preventDefault();
                            wrapSelection('_');
                            return;
                        }
                    }

                    if (event.key !== 'Enter') {
                        return;
                    }

                    if (event.shiftKey || event.ctrlKey) {
                        return;
                    }

                    if (!value.trim()) {
                        onChange('');
                        return;
                    }

                    event.preventDefault();
                    onSubmit?.(getSerializedValue());
                }}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
                className={classNames('classForInsertingFiles', 'new-design-scroll', s.messageTextarea)}
            />
            {isEmojiPickerOpen && (
                    <div className={s.emojiPickerPopup} ref={emojiPickerPopupRef}>
                        <EmojiPicker
                            onEmojiClick={onEmojiClick}
                            theme={Theme.LIGHT}
                            emojiData={ru}
                            searchDisabled
                            skinTonesDisabled
                            lazyLoadEmojis
                            previewConfig={{ showPreview: false }}
                            width={280}
                            height={380}
                        />
                    </div>
            )}
            <div className={s.emojiPickerWrapper} ref={emojiPickerRef}>
                
                <button
                    type='button'
                    className={classNames(s.emojiButton, {[s.emojiButtonActive]: isEmojiPickerOpen})}
                    disabled={disabled}
                    onClick={() => setIsEmojiPickerOpen((prev) => !prev)}>
                    <EmojiIcon />
                </button>
            </div>
        </div>
    );
});

export default ChatTextInput;
